// PendingDetailPage — read-only view of a pending booking with the action
// stack (Edit, Follow-up, Final Notice, Cancel, Delete (admin), Book In)
// and an inline Notes section.
//
// Mirrors the legacy `get_pending_data.php` HTML output: Customer /
// Vehicle / Booking Details cards, plus a conditional Collection Address
// card when collection differs from customer address.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightCircle,
  ChevronLeft,
  Pencil,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { CancelPendingModal } from './components/CancelPendingModal';
import { ConvertPendingModal } from './components/ConvertPendingModal';
import { DeletePendingConfirm } from './components/DeletePendingConfirm';
import { FollowupPendingModal, type FollowupTab } from './components/FollowupPendingModal';
import { PendingNotes } from './components/PendingNotes';
import { StatusChangePendingModal } from './components/StatusChangePendingModal';
import { usePendingDetail } from './hooks/usePending';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../api/queryKeys';
import type { PendingStatus, PendingSummary } from '../../types/api';
import styles from './PendingDetailPage.module.css';

const STATUS_VARIANTS: Record<PendingStatus, BadgeVariant> = {
  pending: 'warning',
  final_notice: 'danger',
  booked: 'success',
  cancelled: 'muted',
};

const STATUS_LABELS: Record<PendingStatus, string> = {
  pending: 'Pending',
  final_notice: 'Final Notice',
  booked: 'Booked',
  cancelled: 'Cancelled',
};

function formatPrice(p: number | null): string {
  if (p === null) return '—';
  return `£${p.toFixed(2)}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PendingDetailPage(): JSX.Element {
  const { id: encId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const detail = usePendingDetail(encId);

  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupTab, setFollowupTab] = useState<FollowupTab>('regular');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="md" />
          <span>Loading pending booking…</span>
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Pending booking" subtitle="Could not load this pending booking." />
        <Button variant="secondary" onClick={() => navigate('/collections/pending')}>
          <ChevronLeft size={14} aria-hidden /> Back to pending list
        </Button>
      </div>
    );
  }

  const pending: PendingSummary = detail.data.pending_booking;
  const terminal = pending.status === 'booked' || pending.status === 'cancelled';
  const showCollection =
    pending.collection_address !== null &&
    pending.collection_address !== pending.address;

  function openFollowup(tab: FollowupTab) {
    setFollowupTab(tab);
    setFollowupOpen(true);
  }

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
  }

  function handleDeleted() {
    navigate('/collections/pending');
  }

  function handleCancelled() {
    refresh();
  }

  return (
    <div className={styles.page}>
      <Link to="/collections/pending" className={styles.back}>
        <ChevronLeft size={14} aria-hidden /> Back to pending list
      </Link>

      <PageHeader
        title={
          <span className={styles.titleRow}>
            {pending.name}
            <Badge variant={STATUS_VARIANTS[pending.status]} className={styles.statusBadge}>
              {STATUS_LABELS[pending.status]}
            </Badge>
          </span>
        }
        subtitle={
          <span className={styles.subtitleRow}>
            <span className={styles.refChip}>{pending.reference}</span>
            <span className={styles.uniqueChip}>{pending.unique_id}</span>
          </span>
        }
      />

      <div className={styles.layout}>
        <div className={styles.main}>
          <Card>
            <h3 className={styles.sectionTitle}>Customer</h3>
            <dl className={styles.grid}>
              <div>
                <dt>Full name</dt>
                <dd>{pending.name}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{pending.phone}</dd>
              </div>
              <div>
                <dt>Secondary phone</dt>
                <dd>{pending.phone2 ?? '—'}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{pending.email ?? '—'}</dd>
              </div>
              <div>
                <dt>Postcode</dt>
                <dd>{pending.postcode}</dd>
              </div>
              <div className={styles.fullRow}>
                <dt>Address</dt>
                <dd>{pending.address}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h3 className={styles.sectionTitle}>Vehicle</h3>
            <dl className={styles.grid}>
              <div>
                <dt>Make &amp; model</dt>
                <dd>{pending.car_make_model}</dd>
              </div>
              <div>
                <dt>Registration</dt>
                <dd className={styles.regCell}>{pending.car_registration}</dd>
              </div>
              <div>
                <dt>Price</dt>
                <dd>{formatPrice(pending.price)}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h3 className={styles.sectionTitle}>Booking details</h3>
            <dl className={styles.grid}>
              <div>
                <dt>Product</dt>
                <dd>{pending.product_name ?? '—'}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{pending.source_name ?? '—'}</dd>
              </div>
              <div>
                <dt>Created at</dt>
                <dd>{formatDateTime(pending.created_at)}</dd>
              </div>
              <div>
                <dt>Last updated</dt>
                <dd>{formatDateTime(pending.updated_at)}</dd>
              </div>
              <div>
                <dt>Last follow-up</dt>
                <dd>{formatDateTime(pending.last_followup_at)}</dd>
              </div>
              <div>
                <dt>Final notice sent</dt>
                <dd>{formatDateTime(pending.final_notice_sent_at)}</dd>
              </div>
              {pending.notes !== null && pending.notes.length > 0 ? (
                <div className={styles.fullRow}>
                  <dt>Initial notes</dt>
                  <dd className={styles.preserveLines}>{pending.notes}</dd>
                </div>
              ) : null}
            </dl>
          </Card>

          {showCollection ? (
            <Card>
              <h3 className={styles.sectionTitle}>Collection address</h3>
              <dl className={styles.grid}>
                <div>
                  <dt>Postcode</dt>
                  <dd>{pending.collection_postcode ?? '—'}</dd>
                </div>
                <div className={styles.fullRow}>
                  <dt>Address</dt>
                  <dd>{pending.collection_address}</dd>
                </div>
              </dl>
            </Card>
          ) : null}
        </div>

        <aside className={styles.aside}>
          <Card>
            <h3 className={styles.sectionTitle}>Actions</h3>
            <div className={styles.actionStack}>
              <Button
                variant="secondary"
                onClick={() => navigate(`/collections/pending/${encodeURIComponent(encId ?? '')}/edit`)}
                disabled={terminal}
                aria-label="Edit pending booking"
              >
                <Pencil size={14} aria-hidden /> Edit
              </Button>
              <Button
                variant="primary"
                onClick={() => setConvertOpen(true)}
                disabled={terminal}
                aria-label="Book in"
              >
                <ArrowRightCircle size={14} aria-hidden /> Book In
              </Button>
              <Button
                variant="secondary"
                onClick={() => openFollowup('regular')}
                disabled={terminal}
                aria-label="Send follow-up communication"
              >
                <Send size={14} aria-hidden /> Follow-up
              </Button>
              <Button
                variant="danger"
                onClick={() => setCancelOpen(true)}
                disabled={terminal}
                aria-label="Cancel pending booking"
              >
                <X size={14} aria-hidden /> Cancel
              </Button>
              {isAdmin ? (
                <Button
                  variant="ghost"
                  onClick={() => setDeleteOpen(true)}
                  aria-label="Delete pending booking"
                >
                  <Trash2 size={14} aria-hidden /> Delete
                </Button>
              ) : null}
            </div>
          </Card>

          {pending.status === 'pending' || pending.status === 'final_notice' ? (
            <Card>
              <h3 className={styles.sectionTitle}>Status</h3>
              <button
                type="button"
                className={styles.statusPill}
                onClick={() => setStatusOpen(true)}
                aria-label="Change status"
              >
                <Badge variant={STATUS_VARIANTS[pending.status]}>
                  {STATUS_LABELS[pending.status]}
                </Badge>
                <span className={styles.statusHint}>(click to change)</span>
              </button>
            </Card>
          ) : null}

          <Card>
            <PendingNotes encId={encId} />
          </Card>
        </aside>
      </div>

      <FollowupPendingModal
        open={followupOpen}
        encId={encId ?? null}
        reference={pending.reference}
        initialTab={followupTab}
        onClose={() => setFollowupOpen(false)}
        onSent={refresh}
      />
      <CancelPendingModal
        open={cancelOpen}
        encId={encId ?? null}
        reference={pending.reference}
        onClose={() => setCancelOpen(false)}
        onCancelled={handleCancelled}
      />
      <StatusChangePendingModal
        open={statusOpen}
        encId={encId ?? null}
        currentStatus={pending.status}
        onClose={() => setStatusOpen(false)}
        onChanged={refresh}
      />
      <DeletePendingConfirm
        open={deleteOpen}
        encId={encId ?? null}
        reference={pending.reference}
        onClose={() => setDeleteOpen(false)}
        onDeleted={handleDeleted}
      />
      <ConvertPendingModal
        open={convertOpen}
        pending={pending}
        onClose={() => setConvertOpen(false)}
        onConverted={() => {
          refresh();
          setConvertOpen(false);
        }}
      />
    </div>
  );
}
